import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import useSeo from '@/hooks/useSeo';

export default function CaseVentiladoresSecagemCelulosePage() {
  useSeo({
    title: 'Manutenção e Substituição de Ventiladores Industriais em Secagem de Celulose | 5 Anos de Contrato | Grupo Pilar',
    description: 'Case completo de manutenção, substituição e engenharia de confiabilidade de ventiladores industriais em áreas de secagem de planta de celulose. Cinco anos de atuação contínua, 12 profissionais dedicados, mais de 600 equipamentos atendidos com zero acidentes.',
    keywords: 'ventiladores industriais, ventiladores secagem celulose, substituição de ventilador industrial, manutenção de ventiladores, engenharia de confiabilidade, serviços de campo, içamento de ventilador, instalação de hélice, manutenção mecânica industrial, papel e celulose, Grupo Pilar, Aracruz, ES',
    canonical: 'https://www.pilarmanutencao.com.br/cases/substituicao-instalacao-ventilador-secagem-industrial',
    ogUrl: 'https://www.pilarmanutencao.com.br/cases/substituicao-instalacao-ventilador-secagem-industrial',
    ogImage: 'https://readdy.ai/api/search-image?query=Extremely%20realistic%20professional%20industrial%20photography%20modern%20clean%20organized%20heavy%20industrial%20workshop%20with%20large%20machinery%20hydraulic%20equipment%20steel%20components%20metallic%20surfaces%20professional%20technicians%20in%20safety%20gear%20working%20not%20facing%20camera%20cinematic%20dramatic%20lighting%20gray%20silver%20steel%20tones%20with%20subtle%20warm%20yellow%20accent%20details%20wide%20angle%20widescreen%20composition%20premium%20high%20resolution%20sharp%20depth%20industrial%20maintenance%20engineering%20reliability%20heavy%20mechanics%20pulp%20paper%20mining%20oil%20gas%20sectors%20negative%20space%20for%20title%20overlay%20clean%20backgrounds%20not%20stock%20photo%20not%20illustration%20not%203D%20render&width=1600&height=700&seq=gp-cases-unified-hero-v2&orientation=landscape',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const heroImage = 'https://readdy.ai/api/search-image?query=Extremely%20realistic%20professional%20industrial%20photography%20modern%20clean%20organized%20heavy%20industrial%20workshop%20with%20large%20machinery%20hydraulic%20equipment%20steel%20components%20metallic%20surfaces%20professional%20technicians%20in%20safety%20gear%20working%20not%20facing%20camera%20cinematic%20dramatic%20lighting%20gray%20silver%20steel%20tones%20with%20subtle%20warm%20yellow%20accent%20details%20wide%20angle%20widescreen%20composition%20premium%20high%20resolution%20sharp%20depth%20industrial%20maintenance%20engineering%20reliability%20heavy%20mechanics%20pulp%20paper%20mining%20oil%20gas%20sectors%20negative%20space%20for%20title%20overlay%20clean%20backgrounds%20not%20stock%20photo%20not%20illustration%20not%203D%20render&width=1600&height=700&seq=gp-cases-unified-hero-v2&orientation=landscape';

  return (
    <div className="min-h-screen bg-gp-graphite">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={heroImage}
              alt="Ventilador de secagem industrial instalado e fixado após conclusão dos serviços de substituição pela equipe Pilar"
              title="Manutenção e Substituição de Ventilador de Secagem Industrial — Case Pilar"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gp-graphite/75"></div>
          </div>
          <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 pt-28 pb-16">
            <div className="max-w-4xl">
              <div className="flex items-center gap-3 mb-6 flex-wrap">
                <span className="inline-block px-3 py-1 rounded-sm text-xs font-semibold uppercase tracking-wider bg-gp-blue text-white">
                  Serviços de Campo
                </span>
                <span className="text-gray-400 text-xs uppercase tracking-wider">Papel e Celulose</span>
                <span className="text-gray-500 text-xs">— Aracruz, ES</span>
              </div>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase leading-tight mb-6">
                <span className="text-gp-yellow">Case:</span>{' '}
                Manutenção, Substituição e Confiabilidade de Ventiladores Industriais em Áreas de Secagem de Planta de Celulose
              </h1>
              <p className="text-gray-300 text-lg max-w-2xl leading-relaxed">
                Cinco anos de atuação contínua na manutenção mecânica de ventiladores industriais da área de secagem,
                com equipe dedicada de 12 profissionais, mais de 600 equipamentos atendidos e zero acidentes registrados.
                Inclui substituição completa de ventilador de secagem, içamento, instalação de hélice, fixação estrutural
                e engenharia de confiabilidade aplicada à gestão de ativos críticos.
              </p>
            </div>
          </div>
        </section>

        {/* Visão Geral + Histórico */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Visão Geral e Histórico</h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div className="bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-6 md:p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-information-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-white uppercase mb-2">Dados do Contrato</h3>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-settings-3-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Equipamento</span>
                        <p className="text-gray-300 text-sm">Ventiladores Industriais de Secagem de Grande Porte</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-building-2-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Segmento</span>
                        <p className="text-gray-300 text-sm">Papel e Celulose — uma das maiores plantas do setor</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-tools-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Aplicação</span>
                        <p className="text-gray-300 text-sm">Sistema de ventilação da área de secagem — fábricas A e B, linhas 1 a 5</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-calendar-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Duração</span>
                        <p className="text-gray-300 text-sm">Cinco anos de atuação contínua em contrato de manutenção</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-hammer-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Escopo</span>
                        <p className="text-gray-300 text-sm">Manutenção contínua, retirada, içamento, instalação de hélices, substituição de componentes, alinhamento, análise de vibração e gestão de ativos críticos</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-map-pin-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Local</span>
                        <p className="text-gray-300 text-sm">Aracruz, ES</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                  <div className="h-80 md:h-96 flex items-center justify-center bg-gp-graphite">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/6aaedf6f-c6eb-4a24-b7ae-49fffc804a9a_compressed_RETIRADA-VENTILADOR-SECADOR.webp"
                      alt="Retirada do ventilador existente para início das atividades de substituição e manutenção — operação integrada da equipe Pilar em planta de celulose"
                      title="Retirada do ventilador existente — início da substituição"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Retirada do ventilador existente para início das atividades de substituição e manutenção.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Desafio */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Desafio</h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div className="bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-6 md:p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-alert-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="text-gray-300 text-base leading-relaxed mb-4">
                        A Pilar foi responsável pela manutenção dos ventiladores industriais utilizados no processo
                        de secagem de celulose em uma das maiores plantas industriais do setor. O contrato envolvia
                        atuação contínua em equipamentos críticos das linhas de produção, com atividades realizadas
                        nas áreas de secagem das fábricas A e B, abrangendo os ventiladores dos secadores das
                        linhas 1, 2, 3, 4 e 5.
                      </p>
                      <p className="text-gray-400 text-sm leading-relaxed mb-4">
                        A substituição de um ventilador de secagem de grande porte exigiu planejamento integrado e
                        execução técnica precisa em ambiente operacional. O equipamento precisou ser retirado,
                        transportado e reinstalado com a devida movimentação de cargas pesadas e trabalho em altura.
                      </p>
                      <p className="text-gray-400 text-sm leading-relaxed mb-4">
                        As principais complexidades envolviam:
                      </p>
                      <ul className="space-y-2 mb-4">
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Grande volume de equipamentos em operação contínua — mais de 600 ventiladores atendidos</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Diversidade de modelos e configurações de ventiladores industriais</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Equipamento de grande porte instalado em estrutura elevada com acessos restritos</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Necessidade de içamento controlado com dispositivos de elevação e rigging</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Necessidade de rápida disponibilização dos ativos após manutenção</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Controle de confiabilidade operacional e integração com planejamento da planta</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Instalação da hélice com ajuste de precisão e torque controlado</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                  <div className="h-80 md:h-96 flex items-center justify-center bg-gp-graphite">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/d5e737f2-161a-4971-b4be-fa504b867121_compressed_ICAMENTO-VENTILADOR-SECADOR.webp"
                      alt="Movimentação e içamento do conjunto durante a etapa de substituição do equipamento — equipe Pilar em operação integrada no secador"
                      title="Içamento do ventilador de secagem — substituição industrial"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Movimentação e içamento do conjunto durante a etapa de substituição do equipamento.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Solução Aplicada pela Pilar */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Solução Aplicada pela Pilar</h2>
              </div>
              <div className="bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-6 md:p-8 mb-6">
                <p className="text-gray-300 text-sm leading-relaxed mb-6">
                  A metodologia de atuação da Pilar combinou manutenção contínua programada com intervenções
                  de substituição de componentes críticos, sempre com foco em confiabilidade operacional,
                  segurança e disponibilidade dos equipamentos. As atividades abrangeram todas as etapas
                  do ciclo de manutenção dos ventiladores industriais de secagem.
                </p>
              </div>
              <div className="space-y-4">
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">01</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-sm mb-1">Equipe Dedicada e Planejamento</h4>
                    <p className="text-gray-300 text-sm leading-relaxed">Equipe dedicada de 12 profissionais para execução das atividades de manutenção mecânica, com planejamento detalhado das intervenções e coordenação integrada com a operação industrial.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">02</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-sm mb-1">Retirada e Desmontagem</h4>
                    <p className="text-gray-300 text-sm leading-relaxed">Retirada e desmontagem dos conjuntos de ventiladores com instalação de bloqueios mecânicos de segurança. Içamento controlado do conjunto com dispositivos de elevação, movimentação de cargas pesadas e trabalho em altura com acesso por andaimes e estruturas industriais.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">03</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-sm mb-1">Substituição de Componentes</h4>
                    <p className="text-gray-300 text-sm leading-relaxed">Substituição de componentes mecânicos, recuperação de carcaças quando necessário, instalação de hélices fornecidas pelo cliente com aplicação de torque controlado nos parafusos de fixação, e recebimento de motores revisados para montagem.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">04</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-sm mb-1">Içamento e Posicionamento</h4>
                    <p className="text-gray-300 text-sm leading-relaxed">Içamento controlado do novo ventilador para posicionamento na estrutura de suporte. Alinhamento do ventilador com a estrutura fixa e dutos de ventilação. Fixação estrutural com parafusos de alta resistência e aperto controlado.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">05</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-sm mb-1">Testes, Alinhamento e Análise de Vibração</h4>
                    <p className="text-gray-300 text-sm leading-relaxed">Testes dos conjuntos, alinhamento mecânico com instrumentação de precisão, medições de vibração para verificação da condição dos ativos e validação do balanceamento do conjunto rotativo.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">06</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-sm mb-1">Remontagem e Retorno à Operação</h4>
                    <p className="text-gray-300 text-sm leading-relaxed">Remontagem dos equipamentos e retorno dos ventiladores à operação. Verificação do nivelamento e estabilidade do conjunto instalado. Inspeção final dos componentes periféricos e conexões antes da liberação operacional.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">07</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-sm mb-1">Acompanhamento Contínuo</h4>
                    <p className="text-gray-300 text-sm leading-relaxed">Acompanhamento constante da condição dos ativos e organização logística para atender diferentes características técnicas dos ventiladores. Suporte às estratégias de manutenção da planta e gestão de grande volume de ativos industriais.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Instalação da Hélice */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Instalação da Hélice</h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div className="bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-6 md:p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-tools-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-white uppercase mb-2">Montagem de Precisão</h3>
                    </div>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed mb-4">
                    A instalação da hélice representou uma etapa crítica da substituição do ventilador de
                    secagem. A equipe Pilar executou a montagem do conjunto de ventilação com atenção aos
                    ajustes de precisão, torque de fixação e verificação do balanceamento do conjunto rotativo.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Posicionamento da hélice no eixo do conjunto de ventilação com verificação dimensional</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Aplicação de torque controlado nos parafusos de fixação conforme especificação técnica</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Verificação do encaixe e alinhamento da hélice no eixo do conjunto rotativo</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Inspeção visual e dimensional do conjunto montado antes da fixação final</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Preparação do conjunto para a etapa de posicionamento e fixação estrutural</span>
                    </li>
                  </ul>
                </div>
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                  <div className="h-80 md:h-96 flex items-center justify-center bg-gp-graphite">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/b45424a0-6e9b-402e-aa25-8c0b29862972_compressed_INSTALACAO-HELICE-VENTILADOR-SECADOR.webp"
                      alt="Instalação da hélice e montagem do conjunto de ventilação industrial — montagem mecânica de precisão com torquímetro pela equipe Pilar"
                      title="Instalação da hélice do ventilador de secagem"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Instalação da hélice e montagem do conjunto de ventilação industrial.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Montagem Final */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Montagem Final</h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter order-2 lg:order-1">
                  <div className="h-80 md:h-96 flex items-center justify-center bg-gp-graphite">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/314e39dc-edf5-4623-964b-094d4d36cd11_compressed_ICAMENTO-VENTILADOR-SECADOR1.webp"
                      alt="Posicionamento final do ventilador durante a fase de montagem e alinhamento — içamento de ventilador em altura com uso de EPI pela equipe Pilar"
                      title="Posicionamento final do ventilador — montagem e alinhamento"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Posicionamento final do ventilador durante a fase de montagem e alinhamento.
                    </p>
                  </div>
                </div>
                <div className="bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-6 md:p-8 order-1 lg:order-2">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-settings-3-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-white uppercase mb-2">Posicionamento e Alinhamento</h3>
                    </div>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed mb-4">
                    A fase final de montagem envolveu o posicionamento preciso do ventilador na estrutura
                    de suporte, alinhamento do conjunto e fixação definitiva dos componentes. A equipe Pilar
                    executou a etapa com controle dimensional e verificação de todos os pontos de ancoragem.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Içamento controlado do conjunto para posicionamento na estrutura de suporte</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Alinhamento do ventilador com a estrutura fixa e dutos de ventilação</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Fixação estrutural com parafusos de alta resistência e aperto controlado</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Verificação do nivelamento e estabilidade do conjunto instalado</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Inspeção final dos componentes periféricos e conexões antes da liberação</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Segurança e Confiabilidade */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Segurança e Confiabilidade</h2>
              </div>
              <div className="relative rounded-lg overflow-hidden border border-gp-yellow/20 bg-gp-graphite p-6 md:p-8">
                <div className="absolute top-0 left-0 w-1 h-full bg-gp-yellow"></div>
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-bold text-white uppercase mb-2">Resultados de Segurança</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      Um dos principais resultados do contrato foi a manutenção dos mais elevados padrões
                      de segurança operacional durante toda a execução dos serviços ao longo de cinco anos.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="flex items-start gap-3 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-4">
                    <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-shield-check-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <span className="text-gp-yellow font-semibold text-sm">Zero Acidentes</span>
                      <p className="text-gray-400 text-sm">Zero acidentes registrados durante toda a execução do contrato de cinco anos</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-4">
                    <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-shield-check-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <span className="text-gp-yellow font-semibold text-sm">Zero Incidentes</span>
                      <p className="text-gray-400 text-sm">Zero incidentes registrados durante todo o período de atuação contínua</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-4">
                    <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-refresh-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <span className="text-gp-yellow font-semibold text-sm">Continuidade</span>
                      <p className="text-gray-400 text-sm">Continuidade operacional dos equipamentos mantida durante toda a vigência do contrato</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-4">
                    <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-user-heart-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <span className="text-gp-yellow font-semibold text-sm">Suporte Técnico</span>
                      <p className="text-gray-400 text-sm">Suporte às estratégias de manutenção e confiabilidade da planta industrial</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-4">
                    <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <span className="text-gp-yellow font-semibold text-sm">Disponibilidade</span>
                      <p className="text-gray-400 text-sm">Alta disponibilidade dos ventiladores para operação contínua do processo de secagem</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Indicadores e Resultado */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Indicadores do Projeto</h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div className="relative rounded-lg overflow-hidden border border-gp-yellow/20 bg-gp-graphite-light p-6 md:p-8">
                  <div className="absolute top-0 left-0 w-1 h-full bg-gp-yellow"></div>
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-trophy-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h3 className="font-display text-xl font-bold text-white uppercase mb-2">Números do Contrato</h3>
                    </div>
                  </div>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-calendar-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">5 Anos</span>
                        <p className="text-gray-400 text-sm">Atuação contínua no contrato de manutenção</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-team-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">12 Profissionais</span>
                        <p className="text-gray-400 text-sm">Equipe dedicada exclusivamente ao contrato</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-settings-3-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">600+ Equipamentos</span>
                        <p className="text-gray-400 text-sm">Ventiladores industriais atendidos ao longo do contrato</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-file-list-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Linhas 1 a 5</span>
                        <p className="text-gray-400 text-sm">Atendimento às linhas de secagem das fábricas A e B</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-pulse-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Análise de Vibração</span>
                        <p className="text-gray-400 text-sm">Monitoramento contínuo da condição dos ativos</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-compass-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Alinhamento Mecânico</span>
                        <p className="text-gray-400 text-sm">Alinhamento dos conjuntos para performance operacional</p>
                      </div>
                    </li>
                  </ul>
                </div>
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                  <div className="h-80 md:h-96 flex items-center justify-center bg-gp-graphite">
                    <img
                      src={heroImage}
                      alt="Ventilador de secagem instalado, alinhado e pronto para operação industrial — resultado final da manutenção e substituição pela Pilar"
                      title="Ventilador de secagem instalado e pronto para operação"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Ventilador instalado e disponível para operação — resultado final dos serviços executados.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Resultado Final */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Resultado Final</h2>
              </div>
              <div className="bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-6 md:p-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-bold text-white uppercase mb-2">Conclusão dos Serviços</h3>
                  </div>
                </div>
                <p className="text-gray-300 text-sm leading-relaxed mb-4">
                  Cinco anos de atuação contínua com equipe dedicada de 12 profissionais. Mais de 600
                  ventiladores industriais atendidos ao longo do contrato. Atendimento às linhas de secagem
                  das fábricas A e B. Monitoramento por análise de vibração e alinhamento mecânico dos
                  conjuntos. Apoio às substituições de motores e hélices. Gestão de grande volume de ativos
                  industriais com foco em confiabilidade operacional.
                </p>
                <p className="text-gray-300 text-sm leading-relaxed mb-4">
                  A substituição completa do ventilador de secagem industrial foi concluída com sucesso,
                  incluindo retirada do equipamento existente, içamento controlado, instalação da hélice
                  com torque controlado e alinhamento verificado, posicionamento estrutural e fixação
                  definitiva. O ventilador foi liberado para operação com todas as verificações de segurança
                  e qualidade concluídas.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
                  <div className="flex items-center gap-3 bg-gp-graphite-light border border-gp-yellow/20 rounded-lg p-3">
                    <i className="ri-checkbox-circle-line text-gp-yellow w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                    <span className="text-gray-300 text-sm">Zero acidentes durante toda a execução do contrato</span>
                  </div>
                  <div className="flex items-center gap-3 bg-gp-graphite-light border border-gp-yellow/20 rounded-lg p-3">
                    <i className="ri-checkbox-circle-line text-gp-yellow w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                    <span className="text-gray-300 text-sm">Zero incidentes registrados em cinco anos</span>
                  </div>
                  <div className="flex items-center gap-3 bg-gp-graphite-light border border-gp-yellow/20 rounded-lg p-3">
                    <i className="ri-checkbox-circle-line text-gp-yellow w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                    <span className="text-gray-300 text-sm">Continuidade operacional dos equipamentos mantida</span>
                  </div>
                  <div className="flex items-center gap-3 bg-gp-graphite-light border border-gp-yellow/20 rounded-lg p-3">
                    <i className="ri-checkbox-circle-line text-gp-yellow w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                    <span className="text-gray-300 text-sm">Disponibilidade dos ventiladores para operação</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Galeria Técnica */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Galeria Técnica</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Foto 1 — Fixação */}
                <div className="relative rounded-lg border border-gp-graphite-lighter bg-gp-graphite">
                  <div className="h-64 md:h-72 flex items-center justify-center">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/622a3ee8-967d-4bb6-8d87-2f4ff589f815_compressed_FIXACAO-VENTILADOR-SECADOR.webp"
                      alt="Ventilador de secagem instalado e fixado após conclusão dos serviços de substituição"
                      title="Ventilador de secagem instalado e fixado"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 1</span>
                  </div>
                </div>
                {/* Foto 2 — Retirada */}
                <div className="relative rounded-lg border border-gp-graphite-lighter bg-gp-graphite">
                  <div className="h-64 md:h-72 flex items-center justify-center">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/6aaedf6f-c6eb-4a24-b7ae-49fffc804a9a_compressed_RETIRADA-VENTILADOR-SECADOR.webp"
                      alt="Retirada do ventilador existente para início das atividades de substituição"
                      title="Retirada do ventilador existente"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 2</span>
                  </div>
                </div>
                {/* Foto 3 — Içamento */}
                <div className="relative rounded-lg border border-gp-graphite-lighter bg-gp-graphite">
                  <div className="h-64 md:h-72 flex items-center justify-center">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/d5e737f2-161a-4971-b4be-fa504b867121_compressed_ICAMENTO-VENTILADOR-SECADOR.webp"
                      alt="Movimentação e içamento do conjunto durante a etapa de substituição do equipamento"
                      title="Içamento do ventilador de secagem"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 3</span>
                  </div>
                </div>
                {/* Foto 4 — Instalação Hélice */}
                <div className="relative rounded-lg border border-gp-graphite-lighter bg-gp-graphite">
                  <div className="h-64 md:h-72 flex items-center justify-center">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/b45424a0-6e9b-402e-aa25-8c0b29862972_compressed_INSTALACAO-HELICE-VENTILADOR-SECADOR.webp"
                      alt="Instalação da hélice e montagem do conjunto de ventilação industrial"
                      title="Instalação da hélice do ventilador"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 4</span>
                  </div>
                </div>
                {/* Foto 5 — Posicionamento Final */}
                <div className="relative rounded-lg border border-gp-graphite-lighter bg-gp-graphite">
                  <div className="h-64 md:h-72 flex items-center justify-center">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/314e39dc-edf5-4623-964b-094d4d36cd11_compressed_ICAMENTO-VENTILADOR-SECADOR1.webp"
                      alt="Posicionamento final do ventilador durante a fase de montagem e alinhamento"
                      title="Posicionamento final do ventilador"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 5</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Diferenciais da Pilar */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Diferenciais da Pilar</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-2-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Experiência em plantas de papel e celulose com atuação contínua em contratos de manutenção de longo prazo</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-team-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Equipes dedicadas em contratos industriais com gestão de grande volume de ativos críticos</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-settings-3-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Conhecimento em equipamentos rotativos, ventiladores industriais e manutenção mecânica</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-calendar-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Planejamento de intervenções e integração com equipes de manutenção e operação do cliente</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-tools-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Experiência comprovada em substituição de ventiladores industriais com içamento de cargas pesadas</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Segurança operacional como prioridade — zero acidentes durante cinco anos de contrato</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Serviços Relacionados */}
        <section className="relative py-16 overflow-hidden border-t border-gp-graphite-lighter">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-lg font-bold text-white uppercase tracking-wider">Serviços Relacionados</h2>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/mecanica-industrial"
                  className="inline-flex items-center gap-2 bg-gp-graphite border border-gp-graphite-lighter hover:border-gp-yellow/30 px-5 py-3 rounded-lg text-gray-300 hover:text-gp-yellow text-sm font-medium transition-all duration-300"
                >
                  <i className="ri-arrow-right-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  Mecânica Industrial
                </Link>
                <Link
                  to="/servicos-de-campo"
                  className="inline-flex items-center gap-2 bg-gp-graphite border border-gp-graphite-lighter hover:border-gp-yellow/30 px-5 py-3 rounded-lg text-gray-300 hover:text-gp-yellow text-sm font-medium transition-all duration-300"
                >
                  <i className="ri-arrow-right-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  Serviços de Campo
                </Link>
                <Link
                  to="/caldeiraria-solda"
                  className="inline-flex items-center gap-2 bg-gp-graphite border border-gp-graphite-lighter hover:border-gp-yellow/30 px-5 py-3 rounded-lg text-gray-300 hover:text-gp-yellow text-sm font-medium transition-all duration-300"
                >
                  <i className="ri-arrow-right-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  Caldeiraria e Solda
                </Link>
                <Link
                  to="/cases"
                  className="inline-flex items-center gap-2 bg-gp-graphite border border-gp-graphite-lighter hover:border-gp-yellow/30 px-5 py-3 rounded-lg text-gray-300 hover:text-gp-yellow text-sm font-medium transition-all duration-300"
                >
                  <i className="ri-arrow-right-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  Mais Cases de Sucesso
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="relative py-20 bg-gp-graphite-light overflow-hidden">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="relative rounded-xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-gp-yellow/20 to-gp-graphite"></div>
              <div className="absolute inset-0 bg-gp-graphite/60"></div>
              <div className="relative z-10 p-8 md:p-12 text-center">
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase mb-4">
                  Precisa de manutenção ou substituição de ventiladores industriais?
                </h2>
                <p className="text-gray-300 max-w-2xl mx-auto mb-8 leading-relaxed">
                  Conte com a experiência da Pilar em serviços de campo, manutenção de equipamentos rotativos,
                  substituição de ventiladores e engenharia de confiabilidade. Cinco anos de atuação contínua
                  em planta de celulose, 12 profissionais dedicados e zero acidentes.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a
                    href="https://wa.me/5527993092922"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 bg-gp-yellow hover:bg-gp-yellow-dark text-gp-graphite px-8 py-4 rounded-full font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap"
                  >
                    <div className="w-10 h-10 bg-gp-graphite rounded-full flex items-center justify-center">
                      <i className="ri-whatsapp-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <span>Solicitar Orçamento</span>
                    <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
                  </a>
                  <Link
                    to="/contato"
                    className="inline-flex items-center justify-center gap-2 border border-gray-500 hover:border-gp-yellow text-gray-300 hover:text-gp-yellow px-8 py-4 rounded-full font-semibold text-sm transition-all duration-300 whitespace-nowrap"
                  >
                    <i className="ri-mail-line text-lg w-6 h-6 flex items-center justify-center"></i>
                    Falar com Especialista
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Back to Cases */}
        <section className="py-10 border-t border-gp-graphite-lighter">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <Link
                to="/cases"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider"
              >
                <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
                Voltar para Cases
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider"
              >
                <i className="ri-home-line w-5 h-5 flex items-center justify-center"></i>
                Voltar para Home
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />

      {/* Schema JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'Manutenção, Substituição e Confiabilidade de Ventiladores Industriais em Áreas de Secagem de Planta de Celulose | Case Grupo Pilar',
            description: 'Case completo de manutenção, substituição e engenharia de confiabilidade de ventiladores industriais em áreas de secagem de planta de celulose. Cinco anos de atuação contínua, 12 profissionais dedicados, mais de 600 equipamentos atendidos com zero acidentes.',
            image: heroImage,
            author: {
              '@type': 'Organization',
              name: 'Grupo Pilar',
              url: 'https://www.pilarmanutencao.com.br',
            },
            publisher: {
              '@type': 'Organization',
              name: 'Grupo Pilar',
              logo: {
                '@type': 'ImageObject',
                url: '/images/MARCA/LOGO-GRUPO-PILAR/LOGO-GRUPO-PILAR.png',
              },
            },
            datePublished: '2026-06-23',
            dateModified: '2026-06-23',
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': 'https://www.pilarmanutencao.com.br/cases/substituicao-instalacao-ventilador-secagem-industrial',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: 'https://www.pilarmanutencao.com.br',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Cases de Sucesso',
                item: 'https://www.pilarmanutencao.com.br/cases',
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: 'Manutenção, Substituição e Confiabilidade de Ventiladores Industriais',
                item: 'https://www.pilarmanutencao.com.br/cases/substituicao-instalacao-ventilador-secagem-industrial',
              },
            ],
          }),
        }}
      />
    </div>
  );
}